import React, { useState, useEffect, useRef } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons,
  IonBackButton, IonButton, IonSpinner, IonIcon,
} from '@ionic/react';
import { close, settingsOutline } from 'ionicons/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useCreateEvent, useCreateTrade } from '../hooks/useInventory';
import { usePreferredGames } from '../hooks/useGames';
import { useCards } from '../hooks/useCards';
import { TradeCardOut, TradeCardIn } from '../types';

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, fontSize: 14,
  fontWeight: 600, color: 'var(--ion-color-medium)',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 16,
  border: '1px solid var(--ion-color-medium)', borderRadius: 8,
  background: 'var(--ion-item-background, #fff)',
  color: 'var(--ion-text-color)',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = { ...inputStyle, appearance: 'auto' as any };

const fieldGroup: React.CSSProperties = { marginBottom: 12 };

const chipStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '4px 10px', borderRadius: 16, fontSize: 13,
  background: 'var(--ion-color-light)', color: 'var(--ion-text-color)',
  marginRight: 6, marginBottom: 6,
};

const EventForm: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const createEvent = useCreateEvent();
  const createTrade = useCreateTrade();
  const { data: games = [] } = usePreferredGames();
  const { data: cardsData } = useCards({ per_page: 100 });
  const cards = cardsData?.items || [];
  const preloadedRef = useRef(false);

  const [form, setForm] = useState({
    event_type: 'trade' as 'trade' | 'loss' | 'sale',
    card_id: undefined as number | undefined,
    card_name: '',
    game_name: '',
    quantity: 1,
    value: '' as string | number,
    trade_in_card_name: '',
    trade_in_value: '' as string | number,
    auto_add_trade_in: false,
    sale_price: '' as string | number,
    notes: '',
  });

  // Multi-card trade state
  const [cardsOut, setCardsOut] = useState<TradeCardOut[]>([]);
  const [cardsIn, setCardsIn] = useState<TradeCardIn[]>([]);
  const [cashOut, setCashOut] = useState<string>('');
  const [cashIn, setCashIn] = useState<string>('');
  const [tradeNotes, setTradeNotes] = useState('');

  // Cards Out - add immediately on select (no separate Add button needed)

  // Cards In add form
  const [inCardName, setInCardName] = useState('');
  const [inGameName, setInGameName] = useState('');
  const [inQuantity, setInQuantity] = useState(1);
  const [inValue, setInValue] = useState<string>('');
  const [inAutoAdd, setInAutoAdd] = useState(false);

  // Pre-populate card out from query parameter
  useEffect(() => {
    if (preloadedRef.current || cards.length === 0) return;
    const params = new URLSearchParams(location.search);
    const cardOutId = params.get('cardOut');
    if (cardOutId) {
      const card = cards.find((c) => c.id === Number(cardOutId));
      if (card) {
        setCardsOut((prev) => {
          if (prev.some((co) => co.card_id === card.id)) return prev;
          return [...prev, {
            card_id: card.id,
            card_name: card.name,
            game_name: card.game?.name || '',
            quantity: 1,
            value: card.estimated_value,
          }];
        });
        preloadedRef.current = true;
      }
    }
  }, [cards, location.search]);

  const handleCardSelect = (cardId: string) => {
    if (!cardId) {
      setForm((f) => ({ ...f, card_id: undefined }));
      return;
    }
    const card = cards.find((c) => c.id === Number(cardId));
    if (card) {
      setForm((f) => ({
        ...f,
        card_id: card.id,
        card_name: card.name,
        game_name: card.game?.name || '',
        value: card.estimated_value ?? '',
      }));
    }
  };

  const handleAddCardOut = (cardId: string) => {
    if (!cardId) return;
    const card = cards.find((c) => c.id === Number(cardId));
    if (!card) return;
    if (cardsOut.some((co) => co.card_id === card.id)) return;
    setCardsOut((prev) => [...prev, {
      card_id: card.id,
      card_name: card.name,
      game_name: card.game?.name || '',
      quantity: 1,
      value: card.estimated_value,
    }]);
  };

  const handleRemoveCardOut = (cardId: number) => {
    setCardsOut((prev) => prev.filter((c) => c.card_id !== cardId));
  };

  const handleAddCardIn = () => {
    if (!inCardName || !inGameName) return;
    setCardsIn((prev) => [...prev, {
      card_name: inCardName,
      game_name: inGameName,
      quantity: inQuantity,
      value: inValue !== '' ? Number(inValue) : undefined,
      auto_add: inAutoAdd,
    }]);
    setInCardName('');
    setInValue('');
    setInQuantity(1);
    setInAutoAdd(false);
  };

  const handleRemoveCardIn = (index: number) => {
    setCardsIn((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (form.event_type === 'trade') {
      await createTrade.mutateAsync({
        cards_out: cardsOut,
        cards_in: cardsIn,
        cash_out: cashOut !== '' ? Number(cashOut) : undefined,
        cash_in: cashIn !== '' ? Number(cashIn) : undefined,
        notes: tradeNotes || undefined,
      });
    } else {
      await createEvent.mutateAsync({
        event_type: form.event_type,
        card_id: form.card_id,
        card_name: form.card_name,
        game_name: form.game_name,
        quantity: form.quantity,
        value: form.value !== '' ? Number(form.value) : undefined,
        sale_price: form.event_type === 'sale' && form.sale_price !== '' ? Number(form.sale_price) : undefined,
        notes: form.notes || undefined,
      });
    }
    history.goBack();
  };

  const isTrade = form.event_type === 'trade';
  const isTradeValid = isTrade && cardsOut.length > 0 && cardsIn.length > 0;
  const isNonTradeValid = !isTrade && form.card_name && form.game_name;
  const isValid = isTradeValid || isNonTradeValid;
  const isPending = createEvent.isPending || createTrade.isPending;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/history" />
          </IonButtons>
          <IonTitle>Record Event</IonTitle>
          <IonIcon
            icon={settingsOutline}
            slot="end"
            onClick={() => history.push('/settings')}
            style={{ fontSize: 24, cursor: 'pointer', color: '#fff', marginRight: 16 }}
          />
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
          {/* Event Type */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Event Type *</label>
            <select
              style={selectStyle}
              value={form.event_type}
              onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value as any }))}
            >
              <option value="trade">Trade</option>
              <option value="sale">Sale</option>
              <option value="loss">Loss</option>
            </select>
          </div>

          {isTrade ? (
            <>
              {/* ===== MULTI-CARD TRADE UI ===== */}

              {/* Cards Out */}
              <h3 style={{ marginTop: 8, marginBottom: 8 }}>Cards Out (Giving Away)</h3>
              <div style={fieldGroup}>
                <select
                  style={selectStyle}
                  value=""
                  onChange={(e) => handleAddCardOut(e.target.value)}
                >
                  <option value="">Select card from collection...</option>
                  {cards
                    .filter((c) => !cardsOut.some((co) => co.card_id === c.id))
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.game?.name || 'Unknown'})
                        {c.estimated_value ? ` - $${c.estimated_value.toFixed(2)}` : ''}
                      </option>
                    ))}
                </select>
              </div>
              {cardsOut.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {cardsOut.map((co) => (
                    <span key={co.card_id} style={chipStyle}>
                      {co.card_name} ({co.game_name})
                      {co.value != null ? ` - $${co.value.toFixed(2)}` : ''}
                      <IonIcon
                        icon={close}
                        style={{ cursor: 'pointer', fontSize: 16 }}
                        onClick={() => handleRemoveCardOut(co.card_id)}
                      />
                    </span>
                  ))}
                </div>
              )}

              {/* Cards In */}
              <h3 style={{ marginTop: 16, marginBottom: 8 }}>Cards In (Receiving)</h3>
              <div style={fieldGroup}>
                <label style={labelStyle}>Card Name</label>
                <input
                  style={inputStyle}
                  value={inCardName}
                  onChange={(e) => setInCardName(e.target.value)}
                  placeholder="Name of card received"
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ ...fieldGroup, flex: 1 }}>
                  <label style={labelStyle}>Game</label>
                  <select
                    style={selectStyle}
                    value={inGameName}
                    onChange={(e) => setInGameName(e.target.value)}
                  >
                    <option value="">Select game</option>
                    {games.map((g) => (
                      <option key={g.id} value={g.name}>{g.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ ...fieldGroup, width: 80 }}>
                  <label style={labelStyle}>Qty</label>
                  <input
                    style={inputStyle}
                    type="number"
                    min={1}
                    value={inQuantity}
                    onChange={(e) => setInQuantity(Number(e.target.value) || 1)}
                  />
                </div>
                <div style={{ ...fieldGroup, width: 100 }}>
                  <label style={labelStyle}>Value ($) *</label>
                  <input
                    style={inputStyle}
                    type="number"
                    step="0.01"
                    value={inValue}
                    onChange={(e) => setInValue(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div style={{ ...fieldGroup, display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={inAutoAdd}
                    onChange={(e) => setInAutoAdd(e.target.checked)}
                  />
                  Auto-add to collection
                </label>
              </div>
              <IonButton
                expand="block"
                fill="outline"
                onClick={handleAddCardIn}
                disabled={!inCardName || !inGameName || inValue === ''}
                style={{ marginBottom: 12 }}
              >
                + Add Card to Trade
              </IonButton>
              {cardsIn.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {cardsIn.map((ci, i) => (
                    <span key={i} style={chipStyle}>
                      {ci.card_name} ({ci.game_name})
                      {ci.value != null ? ` - $${ci.value.toFixed(2)}` : ''}
                      {ci.auto_add ? ' [auto-add]' : ''}
                      <IonIcon
                        icon={close}
                        style={{ cursor: 'pointer', fontSize: 16 }}
                        onClick={() => handleRemoveCardIn(i)}
                      />
                    </span>
                  ))}
                </div>
              )}

              {/* Cash Out / Cash In */}
              <h3 style={{ marginTop: 16, marginBottom: 8 }}>Cash Component</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ ...fieldGroup, flex: 1 }}>
                  <label style={labelStyle}>Cash Out ($)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    step="0.01"
                    value={cashOut}
                    onChange={(e) => { setCashOut(e.target.value); if (e.target.value) setCashIn(''); }}
                    disabled={cashIn !== ''}
                    placeholder="Money paid"
                  />
                </div>
                <div style={{ ...fieldGroup, flex: 1 }}>
                  <label style={labelStyle}>Cash In ($)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    step="0.01"
                    value={cashIn}
                    onChange={(e) => { setCashIn(e.target.value); if (e.target.value) setCashOut(''); }}
                    disabled={cashOut !== ''}
                    placeholder="Money received"
                  />
                </div>
              </div>

              {/* Notes */}
              <div style={fieldGroup}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }}
                  value={tradeNotes}
                  onChange={(e) => setTradeNotes(e.target.value)}
                  placeholder="Optional notes about this trade"
                />
              </div>
            </>
          ) : (
            <>
              {/* ===== SALE / LOSS UI (unchanged) ===== */}

              {/* Select from existing cards */}
              <div style={fieldGroup}>
                <label style={labelStyle}>Select from Collection (optional)</label>
                <select
                  style={selectStyle}
                  value={form.card_id || ''}
                  onChange={(e) => handleCardSelect(e.target.value)}
                >
                  <option value="">Type manually below...</option>
                  {cards.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.game?.name || 'Unknown'})
                    </option>
                  ))}
                </select>
              </div>

              {/* Card Name */}
              <div style={fieldGroup}>
                <label style={labelStyle}>Card Name *</label>
                <input
                  style={inputStyle}
                  value={form.card_name}
                  onChange={(e) => setForm((f) => ({ ...f, card_name: e.target.value }))}
                  placeholder="Name of the card"
                />
              </div>

              {/* Game Name */}
              <div style={fieldGroup}>
                <label style={labelStyle}>Game *</label>
                <select
                  style={selectStyle}
                  value={form.game_name}
                  onChange={(e) => setForm((f) => ({ ...f, game_name: e.target.value }))}
                >
                  <option value="">Select a game</option>
                  {games.map((g) => (
                    <option key={g.id} value={g.name}>{g.name}</option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div style={fieldGroup}>
                <label style={labelStyle}>Quantity</label>
                <input
                  style={inputStyle}
                  type="number"
                  min={1}
                  value={form.quantity}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: Number(e.target.value) || 1 }))}
                />
              </div>

              {/* Card Value */}
              <div style={fieldGroup}>
                <label style={labelStyle}>Card Value ($)</label>
                <input
                  style={inputStyle}
                  type="number"
                  step="0.01"
                  value={form.value}
                  onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                  placeholder="Estimated value"
                />
              </div>

              {/* Sale-specific fields */}
              {form.event_type === 'sale' && (
                <div style={fieldGroup}>
                  <label style={labelStyle}>Sale Price ($)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    step="0.01"
                    value={form.sale_price}
                    onChange={(e) => setForm((f) => ({ ...f, sale_price: e.target.value }))}
                    placeholder="Amount received"
                  />
                </div>
              )}

              {/* Notes */}
              <div style={fieldGroup}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional notes about this event"
                />
              </div>
            </>
          )}

          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            style={{ marginTop: 16, marginBottom: 16 }}
          >
            {isPending ? <IonSpinner name="crescent" /> : (isTrade ? 'Record Trade' : 'Record Event')}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default EventForm;
